import { useEffect, useRef, useCallback } from 'react'
import { Howl, Howler } from 'howler'
import * as playerActions from 'context/player/reducer/player.actions'
import {
  TRACK_FREQUENCY_UPDATE,
  TRACK_REQUEST_FREQUENCY,
  PREVIEW_LIMIT_PERCENT
} from './player.utils'
import useToast from 'utils/hooks/use-toast'
import { REGISTER_AUDIO_BOOKS } from './constants'
import { getCookie } from 'utils/cookies'
import { usePlayerContext } from 'context/player'
import type { AudioBook } from 'api/data.types'

export function usePlayer() {
  const soundsRef = useRef<Map<number, Howl>>(new Map())
  const currentSoundIdRef = useRef<number | null>(null)
  const pendingLoadHandlersRef = useRef<Map<number, () => void>>(new Map())
  const trackIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const trackRequestIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  )
  const { toastMessage } = useToast()

  const {
    dispatch,
    state: {
      audioBooks,
      currentChapter,
      isPlaying,
      volume,
      rate,
      targetTrackPecentage,
      forwardTrack,
      backTrack,
      isDragProgressBar,
      slug,
      isPreview,
      hasReachedPreviewLimit
    }
  } = usePlayerContext()

  const token = getCookie('access_token')

  const isPreviewRef = useRef(isPreview)
  const isPlayingRef = useRef(isPlaying)
  isPlayingRef.current = isPlaying
  const audioBooksRef = useRef<AudioBook[]>(audioBooks)
  const previewLimitReachedRef = useRef(hasReachedPreviewLimit)
  const registeredListeningSlugRef = useRef<string | null>(null)

  useEffect(() => {
    isPreviewRef.current = isPreview
  }, [isPreview])

  useEffect(() => {
    audioBooksRef.current = audioBooks
  }, [audioBooks])

  useEffect(() => {
    previewLimitReachedRef.current = hasReachedPreviewLimit
  }, [hasReachedPreviewLimit])

  const getTotalDuration = useCallback(
    () =>
      audioBooksRef.current.reduce((total, book) => total + book.duration, 0),
    []
  )

  const getChapterPreviewCap = useCallback(
    (chapterId: number | null): number => {
      if (!isPreviewRef.current || chapterId === null) return Infinity

      const total = getTotalDuration()
      if (!total) return Infinity

      const limitSeconds = (total * PREVIEW_LIMIT_PERCENT) / 100
      const chapterIndex = audioBooksRef.current.findIndex(
        book => book.id === chapterId
      )
      if (chapterIndex < 0) return Infinity

      const durationBeforeChapter = audioBooksRef.current
        .slice(0, chapterIndex)
        .reduce((sum, book) => sum + book.duration, 0)

      return Math.max(limitSeconds - durationBeforeChapter, 0)
    },
    [getTotalDuration]
  )

  const getCurrentSound = useCallback((): Howl | null => {
    if (currentSoundIdRef.current === null) return null
    return soundsRef.current.get(currentSoundIdRef.current) || null
  }, [])

  const progressAudioRequest = useCallback(
    async (
      bookSlug: string,
      audioBookId: number,
      initialTimestamp: number,
      finalTimestamp: number
    ) => {
      try {
        const response = await fetch(REGISTER_AUDIO_BOOKS, {
          headers: {
            Authorization: 'Bearer ' + token,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: JSON.stringify({
            book_slug: bookSlug,
            audio_book_id: audioBookId,
            initial_timestamp: initialTimestamp,
            final_timestamp: finalTimestamp
          })
        })
        return response
      } catch {
        toastMessage({
          message: 'Não foi possível salvar o progresso do seu audiobook',
          type: 'error'
        })
      }
    },
    [token, toastMessage]
  )

  const progressCurrentAudio = useCallback(() => {
    const sound = getCurrentSound()
    if (!sound) return

    let initialTimestamp = Math.round(sound.seek() - 5)
    initialTimestamp = initialTimestamp <= 0 ? 0 : initialTimestamp
    const finalTimestamp = Math.round(sound.seek())

    progressAudioRequest(
      slug,
      currentSoundIdRef.current,
      initialTimestamp,
      finalTimestamp
    )
  }, [getCurrentSound, slug, progressAudioRequest])

  const clearIntervals = useCallback(() => {
    if (trackIntervalRef.current) {
      clearInterval(trackIntervalRef.current)
      trackIntervalRef.current = null
    }
    if (trackRequestIntervalRef.current) {
      clearInterval(trackRequestIntervalRef.current)
      trackRequestIntervalRef.current = null
    }
  }, [])

  const enforcePreviewLimit = useCallback(
    (sound: Howl): boolean => {
      const cap = getChapterPreviewCap(currentSoundIdRef.current)
      if (cap === Infinity) return false

      if (sound.seek() >= cap) {
        if (sound.seek() > cap) {
          sound.seek(cap)
        }
        sound.pause()
        clearIntervals()
        if (!previewLimitReachedRef.current) {
          previewLimitReachedRef.current = true
          progressCurrentAudio()
          dispatch(playerActions.setPreviewLimitReached(true))
        }
        return true
      }
      return false
    },
    [getChapterPreviewCap, clearIntervals, progressCurrentAudio, dispatch]
  )

  const startTrackProgress = useCallback(
    (sound: Howl) => {
      if (trackIntervalRef.current) clearInterval(trackIntervalRef.current)

      trackIntervalRef.current = setInterval(() => {
        if (enforcePreviewLimit(sound)) return

        if (!isDragProgressBar) {
          dispatch(
            playerActions.updateProgressBar({
              value: (sound.seek() * 100) / sound.duration() || 0,
              isDrag: false
            })
          )
        }
        dispatch(playerActions.updateCurrentTrackTime(sound.seek() || 0))
      }, TRACK_FREQUENCY_UPDATE)
    },
    [isDragProgressBar, dispatch, enforcePreviewLimit]
  )

  const registerListeningStart = useCallback(() => {
    if (registeredListeningSlugRef.current === slug) return
    if (currentSoundIdRef.current === null) return

    registeredListeningSlugRef.current = slug
    progressAudioRequest(slug, currentSoundIdRef.current, 0, 0)
  }, [slug, progressAudioRequest])

  const startTrackRequest = useCallback(
    (sound: Howl) => {
      if (trackRequestIntervalRef.current)
        clearInterval(trackRequestIntervalRef.current)

      registerListeningStart()
      progressCurrentAudio()
      trackRequestIntervalRef.current = setInterval(() => {
        progressCurrentAudio()
      }, TRACK_REQUEST_FREQUENCY)
    },
    [registerListeningStart, progressCurrentAudio]
  )

  const playSound = useCallback(
    (sound: Howl) => {
      sound.play()
      startTrackRequest(sound)
      startTrackProgress(sound)
    },
    [startTrackRequest, startTrackProgress]
  )

  const activateChapterWhenLoaded = useCallback(
    (chapterId: number, shouldPlay: () => boolean, onActivate?: () => void) => {
      const sound = soundsRef.current.get(chapterId)
      if (!sound) return

      if (sound.state() === 'loaded') {
        onActivate?.()
        if (shouldPlay()) playSound(sound)
        return
      }

      const previousHandler = pendingLoadHandlersRef.current.get(chapterId)
      if (previousHandler) sound.off('load', previousHandler)

      const handleLoad = () => {
        pendingLoadHandlersRef.current.delete(chapterId)
        if (currentSoundIdRef.current !== chapterId) return
        dispatch(playerActions.loading(false))
        onActivate?.()
        if (shouldPlay()) playSound(sound)
      }
      pendingLoadHandlersRef.current.set(chapterId, handleLoad)

      dispatch(playerActions.loading(true))
      sound.load()
      sound.once('load', handleLoad)
    },
    [dispatch, playSound]
  )

  const unloadAllSounds = useCallback(() => {
    soundsRef.current.forEach(sound => {
      sound.off()
      sound.unload()
    })
    soundsRef.current.clear()
    pendingLoadHandlersRef.current.clear()
    currentSoundIdRef.current = null
  }, [])

  const getAudioNode = useCallback((howl: Howl): HTMLAudioElement | null => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sounds = (howl as any)._sounds
    if (sounds && sounds.length > 0 && sounds[0]._node) {
      return sounds[0]._node as HTMLAudioElement
    }
    return null
  }, [])

  const preloadAllChapters = useCallback(
    (books: AudioBook[], currentId: number, startAt: number) => {
      unloadAllSounds()
      dispatch(playerActions.loading(true))

      const bookSlug = slug

      books.forEach((book, index) => {
        const nextBook = index < books.length - 1 ? books[index + 1] : null

        const handleChapterEnd = () => {
          progressAudioRequest(bookSlug, book.id, book.duration, book.duration)
          dispatch(playerActions.updateChapterHasListened({ id: book.id }))

          if (nextBook) {
            currentSoundIdRef.current = nextBook.id
            activateChapterWhenLoaded(
              nextBook.id,
              () => isPlayingRef.current,
              () => dispatch(playerActions.selectChapter(nextBook.id))
            )
          } else {
            dispatch(playerActions.pause())
            dispatch(
              playerActions.updateProgressBar({ value: 100, isDrag: false })
            )
          }
        }

        const howl = new Howl({
          src: [book.url],
          html5: true,
          preload: book.id === currentId,
          onloaderror: () => {
            if (currentSoundIdRef.current !== book.id) return
            dispatch(playerActions.loading(false))
            dispatch(playerActions.pause())
            toastMessage({
              message: 'Não foi possível carregar este capítulo',
              type: 'error'
            })
          },
          onplay: () => {
            const node = getAudioNode(howl)
            if (node) {
              node.removeEventListener('ended', handleChapterEnd)
              node.addEventListener('ended', handleChapterEnd)
            }
          },
          onload: () => {
            if (book.id === currentId) {
              dispatch(playerActions.loading(false))
              dispatch(
                playerActions.updateProgressBar({
                  value: (startAt * 100) / howl.duration(),
                  isDrag: false
                })
              )
              dispatch(playerActions.updateCurrentTrackTime(startAt))
            }
          }
        })

        howl.rate(rate)
        if (book.id === currentId) {
          howl.seek(startAt)
        }

        soundsRef.current.set(book.id, howl)
      })

      currentSoundIdRef.current = currentId
    },
    [
      slug,
      rate,
      dispatch,
      unloadAllSounds,
      progressAudioRequest,
      activateChapterWhenLoaded,
      getAudioNode,
      toastMessage
    ]
  )

  const cleanUpSound = useCallback(() => {
    const sound = getCurrentSound()
    if (sound) {
      progressCurrentAudio()
      dispatch(playerActions.updateCurrentChapterProgress(sound.seek()))
      sound.stop()
      dispatch(playerActions.pause())
    }
    clearIntervals()
    unloadAllSounds()
  }, [
    getCurrentSound,
    progressCurrentAudio,
    dispatch,
    clearIntervals,
    unloadAllSounds
  ])

  const previousSlugRef = useRef<string>(slug)

  useEffect(() => {
    if (!audioBooks || audioBooks.length === 0) return

    const isNewBook = previousSlugRef.current !== slug
    if (isNewBook) {
      previousSlugRef.current = slug
      clearIntervals()
      preloadAllChapters(audioBooks, currentChapter.id, currentChapter.stopedAt)
      return
    }

    if (soundsRef.current.size === 0) {
      preloadAllChapters(audioBooks, currentChapter.id, currentChapter.stopedAt)
      return
    }

    const currentSound = getCurrentSound()
    if (currentSound && currentSoundIdRef.current !== currentChapter.id) {
      clearIntervals()
      currentSound.stop()

      currentSoundIdRef.current = currentChapter.id
      activateChapterWhenLoaded(currentChapter.id, () => isPlayingRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChapter, slug])

  useEffect(() => {
    const sound = getCurrentSound()
    if (!sound || audioBooks.length === 0) return

    if (isPlaying) {
      if (previewLimitReachedRef.current) {
        dispatch(playerActions.pause())
        return
      }
      if (sound.state() === 'loaded') {
        playSound(sound)
      }
    } else {
      sound.pause()
      progressCurrentAudio()
      clearIntervals()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying])

  useEffect(() => {
    const sound = getCurrentSound()
    if (!sound) return

    if (backTrack) {
      let back10 = sound.seek() - 10
      back10 = back10 < 0 ? 0 : back10

      sound.seek(back10)
      dispatch(playerActions.backTrack(false))
      dispatch(
        playerActions.updateProgressBar({
          value: (back10 * 100) / sound.duration(),
          isDrag: false
        })
      )
      dispatch(playerActions.updateCurrentTrackTime(back10))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backTrack])

  useEffect(() => {
    const sound = getCurrentSound()
    if (!sound) return

    if (forwardTrack) {
      const cap = getChapterPreviewCap(currentSoundIdRef.current)
      let forward10 = sound.seek() + 10
      forward10 = forward10 > sound.duration() ? sound.duration() : forward10
      forward10 = forward10 > cap ? cap : forward10

      sound.seek(forward10)
      dispatch(playerActions.forwardTrack(false))
      dispatch(
        playerActions.updateProgressBar({
          value: (forward10 * 100) / sound.duration(),
          isDrag: false
        })
      )
      dispatch(playerActions.updateCurrentTrackTime(forward10))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forwardTrack])

  useEffect(() => {
    const sound = getCurrentSound()
    if (sound) {
      const cap = getChapterPreviewCap(currentSoundIdRef.current)
      const target = (sound.duration() * targetTrackPecentage) / 100
      sound.seek(target > cap ? cap : target)
      dispatch(playerActions.updateCurrentTrackTime(sound.seek()))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetTrackPecentage])

  useEffect(() => {
    Howler.volume(volume / 100)
  }, [volume])

  useEffect(() => {
    soundsRef.current.forEach(sound => {
      sound.rate(rate)
    })
  }, [rate])

  useEffect(() => {
    const sound = getCurrentSound()
    if (!sound || audioBooks.length === 0 || !isPlaying) return
    startTrackProgress(sound)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragProgressBar])

  return {
    cleanUpSound
  }
}
